import { MapPin, Info } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import type { WizardData } from './types'

interface StepServiceAreaProps {
  data: WizardData
  onChange: (data: WizardData) => void
}

export function StepServiceArea({ data, onChange }: StepServiceAreaProps) {
  const sa = data.serviceArea
  const setSA = (patch: Partial<typeof sa>) =>
    onChange({ ...data, serviceArea: { ...sa, ...patch } })

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Service Area</h3>
        <p className="text-sm text-muted-foreground">
          Optionally define geographic boundaries for service requests.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="sa-enabled">Enable Service Area Boundaries</Label>
          <p className="text-xs text-muted-foreground">
            Restrict service requests to a specific geographic region
          </p>
        </div>
        <Switch
          id="sa-enabled"
          checked={sa.enabled}
          onCheckedChange={(v) => setSA({ enabled: v })}
        />
      </div>

      {sa.enabled ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>City Name</Label>
              <Input
                value={sa.city_name}
                onChange={(e) => setSA({ city_name: e.target.value })}
                placeholder="e.g. City of Doral"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={sa.description}
                onChange={(e) => setSA({ description: e.target.value })}
                placeholder="Service area description"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Allowed ZIP Codes (comma-separated)</Label>
            <Input
              value={sa.allowed_zip_codes}
              onChange={(e) => setSA({ allowed_zip_codes: e.target.value })}
              placeholder="33122, 33126, 33166, 33172, 33178"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Center Latitude</Label>
              <Input
                type="number"
                step="0.0001"
                value={sa.center_lat || ''}
                onChange={(e) => setSA({ center_lat: parseFloat(e.target.value) || 0 })}
                placeholder="25.8194"
              />
            </div>
            <div className="space-y-2">
              <Label>Center Longitude</Label>
              <Input
                type="number"
                step="0.0001"
                value={sa.center_lng || ''}
                onChange={(e) => setSA({ center_lng: parseFloat(e.target.value) || 0 })}
                placeholder="-80.3553"
              />
            </div>
            <div className="space-y-2">
              <Label>Default Zoom</Label>
              <Input
                type="number"
                value={sa.default_zoom}
                onChange={(e) => setSA({ default_zoom: parseInt(e.target.value) || 12 })}
              />
            </div>
          </div>

          <div>
            <Label className="mb-3 block">Bounds</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">North</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={sa.bounds_north || ''}
                  onChange={(e) => setSA({ bounds_north: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">South</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={sa.bounds_south || ''}
                  onChange={(e) => setSA({ bounds_south: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">East</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={sa.bounds_east || ''}
                  onChange={(e) => setSA({ bounds_east: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">West</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={sa.bounds_west || ''}
                  onChange={(e) => setSA({ bounds_west: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>

          <Card className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-900">
            <CardContent className="pt-4 pb-4 flex items-center gap-3">
              <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0" />
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Service requests with locations outside this boundary will receive a notification
                that the area is not serviced.
              </p>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex items-center justify-center py-8 gap-3">
            <Info className="h-5 w-5 text-muted-foreground shrink-0" />
            <p className="text-sm text-muted-foreground">
              No geographic restrictions — service requests will be accepted from any location.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
